import { useCallback, useEffect, useRef, useState } from 'react'
import { TotpStepUpDialog } from '@/components/TotpStepUpDialog'
import { setStepUpHandler, type StepUpPrompt } from '@/lib/api'
import { stepUpBody } from '@/lib/stepUp'

interface DialogState {
  open: boolean
  busy: boolean
  purpose: string | null
  error: string | null
}

const CLOSED: DialogState = { open: false, busy: false, purpose: null, error: null }

/**
 * The one step-up 2FA dialog of the panel (audit 2026-10-22). Mounted once next to the routes;
 * the shared API client (lib/api.ts) asks it for a code whenever the server answers
 * `X-Step-Up: required | invalid`, then repeats the same request with the code — so payouts,
 * refunds, big credits, admins, gateways and kill switches need no page code of their own.
 * While the request with the code is in flight the dialog shows «busy»; a wrong or already used
 * code comes back as `error` and the field is emptied (a code is single-use).
 */
export function StepUpHost() {
  const [state, setState] = useState<DialogState>(CLOSED)
  const pending = useRef<((code: string | null) => void) | null>(null)

  useEffect(
    () =>
      setStepUpHandler({
        ask: (prompt: StepUpPrompt) =>
          new Promise<string | null>((resolve) => {
            pending.current?.(null) // a second ask replaces an unanswered one
            pending.current = resolve
            setState({ open: true, busy: false, purpose: prompt.purpose, error: prompt.error })
          }),
        done: () => {
          pending.current = null
          setState(CLOSED)
        },
      }),
    [],
  )

  const answer = useCallback((code: string | null) => {
    const resolve = pending.current
    pending.current = null
    if (code === null) setState(CLOSED)
    else setState((s) => ({ ...s, busy: true, error: null }))
    resolve?.(code)
  }, [])

  return (
    <TotpStepUpDialog
      open={state.open}
      title="Подтвердите кодом 2FA"
      body={stepUpBody(state.purpose)}
      busy={state.busy}
      error={state.error}
      onSubmit={(code) => answer(code)}
      onCancel={() => answer(null)}
    />
  )
}
